import { useCallback, useEffect, useState } from "react";
import {
  type AdminLoginRequest,
  loginAdmin,
  toAuthSession,
} from "@/lib/api/auth";
import {
  type AuthSession,
  clearAuthSession,
  isAuthSessionExpired,
  loadAuthSession,
  saveAuthSession,
} from "@/lib/api/auth-session";
import type { ApiError } from "@/lib/api/errors";

export type AuthStatus = "checking" | "authenticated" | "unauthenticated";

export type UseAuthSessionResult = {
  authStatus: AuthStatus;
  authSession: AuthSession | null;
  authBusy: boolean;
  authErrorMessage: string | null;
  authInfoMessage: string | null;
  login: (payload: AdminLoginRequest) => Promise<void>;
  logout: () => void;
  markUnauthorized: (message?: string) => void;
};

export function useAuthSession(): UseAuthSessionResult {
  const [authStatus, setAuthStatus] = useState<AuthStatus>("checking");
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(null);
  const [authInfoMessage, setAuthInfoMessage] = useState<string | null>(null);

  const markUnauthorized = useCallback((message?: string) => {
    clearAuthSession();
    setAuthSession(null);
    setAuthStatus("unauthenticated");
    setAuthInfoMessage(
      message ?? "Sessione scaduta. Effettua nuovamente l'accesso.",
    );
    setAuthErrorMessage(null);
    setAuthBusy(false);
  }, []);

  const logout = useCallback(() => {
    clearAuthSession();
    setAuthSession(null);
    setAuthStatus("unauthenticated");
    setAuthInfoMessage("Sessione terminata.");
    setAuthErrorMessage(null);
    setAuthBusy(false);
  }, []);

  const login = useCallback(async (payload: AdminLoginRequest) => {
    try {
      setAuthBusy(true);
      setAuthErrorMessage(null);
      setAuthInfoMessage(null);
      const tokenResponse = await loginAdmin(payload);
      const session = toAuthSession(tokenResponse);
      saveAuthSession(session);
      setAuthSession(session);
      setAuthStatus("authenticated");
    } catch (error) {
      const apiError = error as ApiError;
      setAuthErrorMessage(apiError.message ?? "Credenziali non valide.");
    } finally {
      setAuthBusy(false);
    }
  }, []);

  useEffect(() => {
    const session = loadAuthSession();
    if (!session) {
      setAuthStatus("unauthenticated");
      return;
    }
    if (isAuthSessionExpired(session)) {
      clearAuthSession();
      setAuthStatus("unauthenticated");
      setAuthInfoMessage("Sessione scaduta. Effettua nuovamente l'accesso.");
      return;
    }

    setAuthSession(session);
    setAuthStatus("authenticated");
  }, []);

  return {
    authStatus,
    authSession,
    authBusy,
    authErrorMessage,
    authInfoMessage,
    login,
    logout,
    markUnauthorized,
  };
}
